import React from 'react';

// Stede's standard for a control that is not ready to be pressed.
// #503, Session 79.1, on Colby's instruction: "use a disabled button with helper
// text explaining what's wrong aka why the button is disabled to help users input
// information correctly."
//
// ────────────────────────────────────────────────────────────────────────────
// WHAT A PERSON EXPERIENCES, AND WHY THE MECHANISM IS NOT PLAIN `disabled`
// ────────────────────────────────────────────────────────────────────────────
//
// The rule is: a control must never accept a press and then do nothing. That is
// what #492 was — a manager pressed "Deny request" with no reason chosen, the
// request correctly stayed pending, and the button read as broken.
//
// The obvious fix is a truly `disabled` button. THE RESEARCH DISAGREED, and the
// disagreement is about the exact failure #492 describes:
//
//   * GOV.UK avoids disabling buttons — hard to see at low vision, and not
//     reachable by keyboard at all.
//   * The UK Parliament design system says not to use the disabled state; let
//     people submit and tell them what is wrong.
//   * GitLab's Pajamas is bluntest: a submit button should not be disabled to
//     represent an incomplete form, because there is "no mechanism to attempt to
//     proceed or remediate unknown errors."
//
// A truly disabled button that somebody TAPS still does nothing. It merely looks
// deliberate. On a phone there is no hover, so a tap on a greyed-out control
// gives silence — which is #492 again, wearing a nicer coat.
//
// `aria-disabled` keeps everything Colby asked for and removes that:
//
//   * it still LOOKS unavailable, and carries the reason in visible text;
//   * it stays focusable, so a keyboard reaches it and a screen reader
//     announces both the state and — through aria-describedby — the reason;
//   * PRESSING IT SAYS WHAT IS WRONG rather than doing nothing.
//
// That last line is why this beats the systems above rather than matching them:
// they mostly leave the button fully enabled and let the person fail, then
// explain. Stede shows the reason before the press AND answers the press.
//
// Colby's decision, Session 79.1: "aria-disabled".
//
// ────────────────────────────────────────────────────────────────────────────
// HOW TO USE IT
// ────────────────────────────────────────────────────────────────────────────
//
//   <ActionButton
//     onClick={submit}
//     blockedReason={jobRefError}        // null/'' when the control is ready
//     busy={saving}
//     busyLabel="Saving..."
//   >
//     Confirm receipt
//   </ActionButton>
//
// `blockedReason` is the single source of truth: pass a sentence and the button
// blocks, renders the sentence beneath itself, and reads that sentence back when
// pressed. Pass nothing and it is an ordinary button.
//
// THE REASON IS RENDERED BENEATH THE BUTTON, NOT IN A `title` TOOLTIP. A tooltip
// needs a hover, and mobile is this app's priority surface — a phone shows it
// never. The sweep at 79.1 found zero existing tooltip-only guards in the app,
// so this codifies what was already the practice rather than changing it.

let seq = 0;
function useReasonId() {
  const ref = React.useRef(null);
  if (ref.current === null) { seq += 1; ref.current = `blocked-reason-${seq}`; }
  return ref.current;
}

// `reasonAlreadyShown` exists because on some screens the sentence is already on
// the page, right beside the field it is about — the receive sheet says what a
// work order number should look like directly under that input, which is a better
// place for it than under a button 200px lower. Repeating it would be noise.
// The button still blocks, still announces, and still answers a press; it simply
// does not print a second copy of a sentence already on screen.
export default function ActionButton({
  onClick,
  blockedReason,        // a sentence, or null/'' when ready
  reasonAlreadyShown = false,
  busy = false,
  busyLabel,
  children,
  style,
  className,
  reasonStyle,
  type = 'button',
  ...rest
}) {
  const blocked = !!blockedReason;
  const reasonId = useReasonId();
  // `busy` is a genuine disable: the press has already been accepted and
  // repeating it would double-submit. That is a different situation from "not
  // ready yet", and it is the one case where plain `disabled` is right.
  const [announced, setAnnounced] = React.useState(false);

  function handleClick(e) {
    if (busy) return;
    if (blocked) {
      // The press is answered rather than swallowed. Without this the control is
      // a disabled button again from the person's point of view.
      e.preventDefault();
      setAnnounced(true);
      return;
    }
    onClick?.(e);
  }

  return (
    <>
      <button
        type={type}
        className={className}
        onClick={handleClick}
        disabled={busy}
        aria-disabled={blocked || undefined}
        aria-describedby={blocked ? reasonId : undefined}
        style={{
          ...(style || {}),
          ...(blocked || busy
            ? { background: '#E0E0E0', color: '#5C5C5C', cursor: busy ? 'not-allowed' : 'pointer', border: 'none' }
            : {}),
        }}
        {...rest}
      >
        {busy && busyLabel ? busyLabel : children}
      </button>
      {blocked && (
        <div
          id={reasonId}
          role={announced ? 'alert' : undefined}
          style={{
            fontSize: 11,
            // #513's colour half, Session 79.22b -- the resting reason was #7A7A7A,
            // 4.29 on white at 11px against the 4.5 floor. #48545F, which four
            // callers already pass through reasonStyle, measures 7.75 on white
            // and 7.16 on the #F4F6F8 panel. Colour only - nothing reflows. The
            // pressed state stays #A32D2D (7.07).
            color: announced ? '#A32D2D' : '#48545F',
            marginTop: 6,
            marginBottom: 4,
            lineHeight: 1.4,
            // Kept in the accessibility tree rather than unmounted when the
            // sentence is already elsewhere on screen: aria-describedby has to
            // point at something that exists, and a screen reader still needs
            // the words even when a sighted person can already see them above.
            ...(reasonAlreadyShown
              ? { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }
              : {}),
            ...(reasonStyle || {}),
          }}
        >
          {blockedReason}
        </div>
      )}
    </>
  );
}
